/**
 * ReviewListCard Component
 *
 * Lightweight mobile card for the Reviews list page.
 * Mirrors ApplicationCard pattern for consistent UX.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ClipboardCheck, Eye, Star } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Card } from './ui/Card';
import type { ReviewListItem } from '../types';
import type { RecommendationLevel } from '../types';

export interface ReviewListCardProps {
  review: ReviewListItem;
  onView: (id: number) => void;
}

const recommendationColors: Record<RecommendationLevel, string> = {
  1: 'bg-red-100 text-red-800',
  2: 'bg-orange-100 text-orange-800',
  3: 'bg-yellow-100 text-yellow-800',
  4: 'bg-blue-100 text-blue-800',
  5: 'bg-green-100 text-green-800',
};

const recommendationLabels: Record<RecommendationLevel, string> = {
  1: 'Not Recommended',
  2: 'Below Average',
  3: 'Average',
  4: 'Above Average',
  5: 'Highly Recommended',
};

export const ReviewListCard: React.FC<ReviewListCardProps> = ({ review, onView }) => {
  const score5 = review.overallScore / 10;
  const percentage = review.overallScore * 2;

  const getScoreColor = () => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-blue-600';
    if (percentage >= 40) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <Card variant="elevated" className="transition-all duration-200 hover:shadow-lg">
      <div className="p-4">
        {/* Header: Candidate Name and View Button */}
        <div className="flex items-start justify-between">
          <div
            className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
            onClick={() => onView(review.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onView(review.id)}
            aria-label={`View review for ${review.candidateName || 'Unknown'}`}
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100">
              <ClipboardCheck className="h-5 w-5 text-purple-600" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              {review.candidateId ? (
                <Link
                  to={`/candidates/${review.candidateId}`}
                  className="font-medium text-primary-600 hover:text-primary-700 block truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  {review.candidateName || 'Unknown Candidate'}
                </Link>
              ) : (
                <p className="font-medium text-gray-900 truncate">
                  {review.candidateName || 'Unknown Candidate'}
                </p>
              )}
              <p className="text-sm text-gray-500 truncate">
                {review.jobTitle || 'Unknown Job'}
              </p>
              {review.staleFlag && (
                <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  Needs Re-review
                </span>
              )}
            </div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onView(review.id);
            }}
            className="rounded-lg p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 active:bg-gray-200 transition-colors duration-150"
            title="View review details"
            aria-label="View review details"
          >
            <Eye className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Details Grid */}
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          {/* Recommendation */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Rating</p>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-3 w-3 ${
                    star <= review.recommendation
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <span
              className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                recommendationColors[review.recommendation]
              }`}
            >
              {recommendationLabels[review.recommendation]}
            </span>
          </div>

          {/* Score */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Score</p>
            <div className="flex items-center gap-1">
              <span className={`font-medium ${getScoreColor()}`}>
                {score5.toFixed(1)}/5
              </span>
              <span className="text-xs text-gray-400">({percentage}%)</span>
            </div>
          </div>

          {/* Model */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Model</p>
            {review.modelTier ? (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  review.modelTier === 'premium'
                    ? 'bg-purple-100 text-purple-800'
                    : review.modelTier === 'standard'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-blue-100 text-blue-800'
                }`}
              >
                {review.modelTier.charAt(0).toUpperCase() + review.modelTier.slice(1)}
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>

          {/* Date */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reviewed</p>
            <span className="text-gray-600">
              {formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}
            </span>
          </div>
        </div>

        {/* Summary Preview */}
        {review.summary && (
          <div className="mt-3 text-xs text-gray-500 line-clamp-2">
            {review.summary}
          </div>
        )}
      </div>
    </Card>
  );
};

ReviewListCard.displayName = 'ReviewListCard';
